import type { ActiveBoost, EnemyDef, EquipmentStats, ItemDef, Player, WeaponClass } from './types';
import { WEAPON_PROFILES } from './types';
import { boostMultiplier } from './boosts';
import { getItem } from './items';
import { getSetBonusValue, hasLegendaryEffect } from './itemSets';
import { getAttackKind } from './meshMapping';
import { researchBonus } from './research';
import { nodeLevel } from './skillWeb';

/** Bonus damage multiplier applied to melee weapons. Compensates for the
 * close-range engagement risk vs. ranged weapons that strike from safety. */
export const MELEE_DAMAGE_BONUS = 1.30;

/** World-units the player must be within for melee swings to land. Beyond
 * this the autoTick is skipped (no swing animation, no damage). Forces
 * the player to physically close the gap to engage with melee weapons. */
export const MELEE_REACH = 1.8;

import { totalAugmentValue } from './augments';
import { totalAspectValue } from './aspects';

export interface PlayerCombatStats {
  maxHp: number;
  attack: number;
  defense: number;
  /** Crit-chance 0..1 — rolled per attack. Default 0 (no crits until augments
   *  / equipment / aspects grant it). */
  critChance: number;
  /** Crit-damage multiplier. Default 1.5 = +50% bonus on a crit. */
  critMult: number;
}

/**
 * Derive combat stats from player level + equipment + active boosts + set bonuses.
 * Defense uses level-scaling: base = 2 + floor(level/3) + equipment + kinetic stacks.
 */
export function calcPlayerStats(
  player: Player,
  equipStats: EquipmentStats,
  activeBoosts: ActiveBoost[] = [],
  now: number = Date.now(),
  kineticStacks: number = 0,
): PlayerCombatStats {
  const atkSetMult = 1 + getSetBonusValue(player.equipment, 'atk_pct');
  const defSetMult = 1 + getSetBonusValue(player.equipment, 'def_pct');
  const hpSetMult  = 1 + getSetBonusValue(player.equipment, 'hp_pct');

  // Legendary: Golden Ledger — +1 ATK per 500k credits (max +200)
  // Note: Phase 6 will replace this with a Skill-Web "Compound Processor" reading from Decrypter buffers.
  const legendaryAtk = hasLegendaryEffect(player.equipment, 'credits_to_atk')
    ? Math.min(200, Math.floor(player.credits / 500_000))
    : 0;

  const research = player.research ?? {};
  const resAtk = researchBonus(research, 'atk_flat');
  const resDef = researchBonus(research, 'def_flat');
  const resHp  = researchBonus(research, 'hp_flat');

  // Level scales base stats on a sqrt curve so endgame is gear-dominant:
  // a naked level-84 character would otherwise out-class even legendary
  // equipment because linear `5 + level` ATK = 89 vs HK-Nail's +32 ATK.
  // Sqrt keeps early-game progression spotted while flattening late so
  // the player's power growth from level alone tapers, forcing gear +
  // augment + aspect investment for endgame content.
  const levelSqrt = Math.sqrt(player.level);
  const baseHp = 50 + Math.floor(levelSqrt * 35) + equipStats.health + resHp;
  const baseDef = 2 + Math.floor(levelSqrt * 1.5) + equipStats.defense + kineticStacks + resDef;
  const baseAtk = 5 + Math.floor(levelSqrt * 4) + equipStats.attack + legendaryAtk + resAtk;

  const atkMult = boostMultiplier(activeBoosts, 'damage', now);
  const defMult = boostMultiplier(activeBoosts, 'defense', now);

  // Skill-Web Core ring (multiplicative): each level adds +5 % to its stat.
  // Will be removed in P2.A.5 (Skill-Web sunset) — the Augment-Tree below
  // takes over as the primary stat-multiplier sink.
  const webHp  = 1 + nodeLevel(player, 'vitality')   * 0.05;
  const webDef = 1 + nodeLevel(player, 'alloy')      * 0.05;
  const webAtk = 1 + nodeLevel(player, 'aggression') * 0.05;

  // P2.A Augment-Tree multipliers — multiplicative with set/web/research.
  // Branches stack additively WITHIN each effect (e.g. Survivor + Aggressor
  // both feeding hp_pct sum), then 1+sum becomes the multiplier.
  const augHpMult  = 1 + totalAugmentValue(player.augments, 'hp_pct');
  const augDefMult = 1 + totalAugmentValue(player.augments, 'def_pct');
  const augAtkMult = 1 + totalAugmentValue(player.augments, 'atk_pct');
  const augCritChance = totalAugmentValue(player.augments, 'crit_chance');
  const augCritMult   = 1.5 + totalAugmentValue(player.augments, 'crit_mult');

  // P2.B Aspekt-Passives — stack additively with Augments on the same effect
  // and then multiply with the final stat. Affinity-multipliers are already
  // baked into totalAspectValue, so callers don't need to know about tiers.
  const aspHpMult  = 1 + totalAspectValue(player, 'hp_pct', false, getItem);
  const aspDefMult = 1 + totalAspectValue(player, 'def_pct', false, getItem);
  const aspAtkMult = 1 + totalAspectValue(player, 'atk_pct', false, getItem);
  const aspCritChance = totalAspectValue(player, 'crit_chance', false, getItem);

  return {
    maxHp:   Math.round(baseHp  * webHp * hpSetMult * augHpMult * aspHpMult),
    attack:  Math.round(baseAtk * atkSetMult * atkMult * webAtk * augAtkMult * aspAtkMult),
    defense: Math.round(baseDef * defSetMult * defMult * webDef * augDefMult * aspDefMult),
    critChance: Math.min(1, Math.max(0, augCritChance + aspCritChance)),
    critMult: augCritMult,
  };
}

/** Effective profile for the equipped weapon — class-defaults overridden by item-specific values. */
export function getWeaponProfile(player: Player): { attackSpeed: number; damageMult: number; armorPenPct: number; isMelee: boolean } {
  const inst = player.equipment.weapon;
  const def = inst ? getItem(inst.itemId) : undefined;
  const klass: WeaponClass = def?.weaponClass ?? 'standard';
  const base = WEAPON_PROFILES[klass];
  // Melee weapons get a damage bonus baked into the profile so all combat call
  // sites (basic swings, specials, shield-bash branches) pick it up automatically.
  const isMelee = getAttackKind(player.equipment) === 'melee';
  const meleeMult = isMelee ? MELEE_DAMAGE_BONUS : 1;
  // P3 Phantom attack_speed_pct: flat permanent attack-speed buff, in
  // contrast to Stormcaller's stack-decay model. Stacks with weapon-class
  // defaults; doesn't multiply itself with on-hit AS-stacks (those are
  // applied separately by the tick driver).
  const asMult = 1 + totalAspectValue(player, 'attack_speed_pct', false, getItem);
  return {
    attackSpeed: (def?.attackSpeed ?? base.attackSpeed) * asMult,
    damageMult:  (def?.damageMult ?? base.damageMult) * meleeMult,
    armorPenPct: def?.armorPenPct ?? 0,
    isMelee,
  };
}

/**
 * v2 damage formula (LoL-style mitigation × WeaponMult).
 *   Damage = ATK × (100 / (100 + targetDEF × (1 − armorPenPct))) × WeaponMult
 *
 * Compared to v1's ATK − DEF subtraction, this scales smoothly even at low ATK
 * vs high DEF, and makes percentage armor-pen meaningful.
 */
export function calcPlayerDamage(
  stats: PlayerCombatStats,
  enemy: EnemyDef,
  weapon?: { damageMult: number; armorPenPct: number },
): number {
  const mult = weapon?.damageMult ?? 1.0;
  const pen  = weapon?.armorPenPct ?? 0;
  const targetDef = enemy.defense * (1 - Math.min(1, Math.max(0, pen)));
  return Math.max(1, Math.round(stats.attack * (100 / (100 + targetDef)) * mult));
}

/**
 * Enemy damage using percentage mitigation (LoL-style):
 *   damage = ceil(enemyATK × atkMult × 100 / (100 + playerDEF))
 *
 * `atkMult` (default 1.0) lets endless mode scale damage per wave without
 * mutating EnemyDef. Callers in dungeonStore pass `s.scaledAtkMult`.
 */
export function calcEnemyDamage(enemy: EnemyDef, stats: PlayerCombatStats, atkMult: number = 1): number {
  return Math.max(1, Math.round(enemy.attack * atkMult * (100 / (100 + stats.defense))));
}

/**
 * Special attack damage. v2: 3× normal damage for ALL classes — Heavy weapons
 * already burst harder per hit through their high `damageMult` (2.5× vs 0.5×),
 * so the old class-conditional 4× multiplier is no longer needed.
 */
export function calcSpecialDamage(
  stats: PlayerCombatStats,
  enemy: EnemyDef,
  weapon?: { damageMult: number; armorPenPct: number },
): number {
  return calcPlayerDamage(stats, enemy, weapon) * 3;
}

/** Shield Bash: special scales with DEF × multiplier instead of ATK. */
export function calcShieldBashDamage(stats: PlayerCombatStats, multiplier: number): number {
  return Math.max(1, Math.round(stats.defense * multiplier));
}

/** Get the weapon class of the player's equipped weapon. */
export function getEquippedWeaponClass(player: Player): WeaponClass {
  const inst = player.equipment.weapon;
  if (!inst) return 'standard';
  return getItem(inst.itemId)?.weaponClass ?? 'standard';
}

/** True if player has the Singularity Edge (armor_penetration) equipped. Legacy — new code reads weaponProfile.armorPenPct. */
export function hasArmorPenetration(player: Player): boolean {
  return hasLegendaryEffect(player.equipment, 'armor_penetration');
}

/** Legacy: get the equipped weapon's ItemDef. Used by ability/special branches that still inspect weapon directly. */
export function getEquippedWeapon(player: Player): ItemDef | undefined {
  const inst = player.equipment.weapon;
  if (!inst) return undefined;
  return getItem(inst.itemId);
}
